import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CreateRecurrenceInboxEntryInput,
  RecurrenceInboxEntry,
} from '@this+that/models';
import log from 'loglevel';

import { useCreateRecurrenceInboxEntry as useCreateService } from '../../services/recurrenceInboxEntries.service';
import { useInboxEntriesStore } from '../../store';
import { useCustomToast } from '../useCustomToast';

interface UseCreateRecurrenceInboxEntryReturn {
  createRecurrenceInboxEntry: (
    input: CreateRecurrenceInboxEntryInput,
  ) => Promise<RecurrenceInboxEntry | null>;
  isCreating: boolean;
}

export function useCreateRecurrenceInboxEntry(): UseCreateRecurrenceInboxEntryReturn {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();

  const { createRecurrenceInboxEntry: createService, loading: isCreating } =
    useCreateService();

  const { addRecurrenceInboxEntry } = useInboxEntriesStore(
    (state) => state.taskManager.activeTaskGroup.recurrenceEntries,
  );

  const createRecurrenceInboxEntry = useCallback(
    async (
      input: CreateRecurrenceInboxEntryInput,
    ): Promise<RecurrenceInboxEntry | null> => {
      try {
        const result = await createService(input);
        if (result) {
          addRecurrenceInboxEntry(result);
          return result;
        }
        errorMessage(t('doBox.recurrences.createError'));
        return null;
      } catch (error) {
        log.error(
          'useCreateRecurrenceInboxEntry: failed to create recurrence:',
          error,
        );
        errorMessage(t('doBox.recurrences.createError'));
        return null;
      }
    },
    [createService, addRecurrenceInboxEntry, errorMessage, t],
  );

  return { createRecurrenceInboxEntry, isCreating };
}
